import React from 'react';
import { navigate } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  brandColours,
  fontSize,
  fontWeights,
  minBreakpointQuery,
  maxBreakpointQuery,
  standardColours,
  standardTransition,
} from '../styles';
import { Link } from './ui';
import { buildUrl } from '../utils';

const StyledFeatureCard = styled.article`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: start;
  padding: 30px;
  background-color: ${brandColours.primary};
  border-radius: 10px;
  cursor: pointer;
  transition: ${standardTransition('background-color')};

  &:hover {
    background-color: ${brandColours.quinary};
  }

  ${({ isFeaturesOverview }) => {
    if (isFeaturesOverview) {
      return css`
        ${minBreakpointQuery.small`
          flex-direction: row;
        `}
      `;
    }
  }}
`;

const StyledIconWrapper = styled.div`
  display: inline-block;
  padding: 20px;
  background-color: ${brandColours.quinary};
  border-radius: 20px;
  flex-shrink: 0;
  border: 2px solid ${standardColours.white};

  ${maxBreakpointQuery.small`
    margin-bottom: 20px;
  `}

  ${minBreakpointQuery.small`
    margin-right: 25px;
    padding: 22px;
  `}

  ${minBreakpointQuery.large`
    padding: 24px;
  `}
`;

const StyledIcon = styled.img`
  ${({ isFeaturesOverview }) => {
    if (isFeaturesOverview) {
      return css`
        height: 36px;
        width: 36px;

        ${minBreakpointQuery.small`
          height: 42px;
          width: 42px;
        `}

        ${minBreakpointQuery.large`
          height: 48px;
          width: 48px;
        `}
      `;
    } else {
      return css`
        height: 30px;
        width: 30px;
      `;
    }
  }}
`;

const StyledContent = styled.div`
  color: ${standardColours.white};
  flex: 1;
  transition: ${standardTransition('transform')};

  ${StyledFeatureCard}:hover & {
    transform: translateY(-4px);
  }
`;

const StyledHeading = styled.h3`
  font-weight: ${fontWeights.medium};

  ${({ isFeaturesOverview }) => {
    if (isFeaturesOverview) {
      return css`
        ${fontSize(21)};

        ${minBreakpointQuery.small`
          ${fontSize(23)};
        `}

        ${minBreakpointQuery.large`
          ${fontSize(25)};
        `}
      `;
    } else {
      return css`
        ${fontSize(18)};
      `;
    }
  }}
`;

const StyledLink = styled(Link)`
  ${({ isFeaturesOverview }) => {
    if (!isFeaturesOverview) {
      return css`
        display: flex;
        align-items: center;
        gap: 12px;
      `;
    }
  }}
`;

const StyledText = styled.p`
  margin-top: 12px;
  ${fontSize(16)};
  line-height: 1.7;

  ${minBreakpointQuery.small`
    margin-top: 14px;
    ${fontSize(17)};
  `}

  ${minBreakpointQuery.large`
    margin-top: 16px;
    ${fontSize(18)};
  `}
`;

const FeatureCard = ({
  heading,
  slug,
  icon,
  text,
  category,
  isFeaturesOverview,
  ...props
}) => {
  const urlPath =
    category && category.slug ? `crm/${category.slug}/${slug}` : `crm/${slug}`;

  return (
    <StyledFeatureCard
      isFeaturesOverview={isFeaturesOverview}
      {...props}
      onClick={() => navigate(buildUrl(urlPath))}
    >
      {isFeaturesOverview && icon && (
        <StyledIconWrapper>
          <StyledIcon src={icon.url} alt={icon.alt} loading="lazy" />
        </StyledIconWrapper>
      )}
      <StyledContent>
        <StyledHeading isFeaturesOverview={isFeaturesOverview}>
          <StyledLink to={urlPath} isFeaturesOverview={isFeaturesOverview}>
            {!isFeaturesOverview && icon && (
              <StyledIcon
                src={icon.url}
                alt={icon.alt}
                loading="lazy"
                isFeaturesOverview={isFeaturesOverview}
              />
            )}
            {heading}
          </StyledLink>
        </StyledHeading>
        {text && <StyledText>{text}</StyledText>}
      </StyledContent>
    </StyledFeatureCard>
  );
};

export default FeatureCard;
